import { FOLLOW_UP_STATE_LABEL, type FollowUp } from '@cyberscore/contracts';
import { CheckCircle2, MessageCircleQuestion } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '../../ui/Toasts';
import { useCanAnswer } from '../assessments/api';
import { useFollowUps, useReplyFollowUp } from './api';

/**
 * For the business (task 87): the questions its insurer's assistant has about its answers, to reply to in
 * its own words. A reply doesn't change an answer by itself; the insurer reads it and decides.
 */
export function FollowUpsForYou({ id, status }: { id: string; status: string }) {
  const shown = status !== 'in_progress' && status !== 'draft';
  const list = useFollowUps(id, shown);
  const canAnswer = useCanAnswer();
  if (!shown || !list.data?.length) return null;
  const open = list.data.filter((f) => f.state === 'open').length;

  return (
    <section className="card pad changes-card" aria-labelledby={`follow-ups-${id}`}>
      <div className="row-between">
        <h2 id={`follow-ups-${id}`} className="section-title row gap">
          <MessageCircleQuestion size={17} /> Questions from your insurer
        </h2>
        <span className={`pill ${open ? 'warn' : 'ok'}`}>{open ? `${open} to answer` : 'All answered'}</span>
      </div>
      <p className="muted small">
        A few of your answers need a word more. Reply in your own words; your insurer reads each reply and
        decides whether the answer should change.
      </p>
      <ol className="changes">
        {list.data.map((f) => (
          <Item key={f.id} id={id} f={f} canAnswer={canAnswer && f.state === 'open'} />
        ))}
      </ol>
    </section>
  );
}

function Item({ id, f, canAnswer }: { id: string; f: FollowUp; canAnswer: boolean }) {
  const reply = useReplyFollowUp(id);
  const toast = useToast();
  const [text, setText] = useState('');
  return (
    <li className={f.state === 'open' ? '' : 'done'}>
      <div className="row gap top">
        {f.state === 'open' ? (
          <span className="dot" aria-hidden />
        ) : (
          <CheckCircle2 size={16} className="ok-t" />
        )}
        <div className="grow stack-sm">
          <div className="qtext">{f.question_text}</div>
          <p className="small muted">You answered: {f.answer_label}</p>
          <p className="small insurer-says">“{f.prompt}”</p>
          {f.reply && (
            <p className="small muted">
              <b>You replied:</b> {f.reply.text}
            </p>
          )}
          {f.state !== 'open' && <span className="pill neutral">{FOLLOW_UP_STATE_LABEL[f.state]}</span>}
          {canAnswer && (
            <form
              className="stack-sm"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!text.trim()) return;
                try {
                  await reply.mutateAsync({ fid: f.id, text: text.trim() });
                  setText('');
                  toast('Reply sent to your insurer', 'ok');
                } catch (err) {
                  toast(err instanceof Error ? err.message : 'Could not send your reply', 'bad');
                }
              }}
            >
              <label className="small" htmlFor={`follow-up-${f.id}`}>
                Your reply
              </label>
              <textarea
                id={`follow-up-${f.id}`}
                className="input"
                rows={2}
                maxLength={2000}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <div>
                <button
                  type="submit"
                  className="btn btn-sm btn-primary"
                  disabled={!text.trim() || reply.isPending}
                >
                  {reply.isPending ? 'Sending…' : 'Send reply'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </li>
  );
}
